import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useQuery, useMutation } from 'react-query';
import { HOSOLUUTRU_API } from '@app/utils/apiUrl';
import { Get, Post, Put } from '@app/utils/httpProvider';
import DocumentInput from '@app/components/document-input/DocumentInput';

const HoSoLuuTruModal = ({ isOpen, onClose, dotTuyenSinhId, tenDot }) => {
    const [fileList, setFileList] = useState([]);
    const [existingRecord, setExistingRecord] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Reset state whenever the dotTuyenSinhId changes so stale data from a
    // previous đợt is never shown while the new query is in flight.
    useEffect(() => {
        setFileList([]);
        setExistingRecord(null);
    }, [dotTuyenSinhId]);

    // Fetch existing records for this DotTuyenSinh
    const { data, refetch, isLoading } = useQuery(
        ['getHoSoLuuTru', dotTuyenSinhId],
        () => {
            if (!dotTuyenSinhId) return Promise.resolve(null);
            return Get(HOSOLUUTRU_API.GET_PAGING, null, {
                DotTuyenSinhId: dotTuyenSinhId,
                PageIndex: 1,
                PageSize: 1
            });
        },
        {
            enabled: isOpen && !!dotTuyenSinhId,
            cacheTime: 0,
            staleTime: 0,
            onSuccess: (res) => {
                const items = res?.data?.$values ? res.data.$values : (res?.data || []);
                if (items && items.length > 0) {
                    const record = items[0];
                    setExistingRecord(record);
                    try {
                        const parsedFiles = record.fileLuuTru || record.FileLuuTru ? JSON.parse(record.fileLuuTru || record.FileLuuTru) : [];
                        const formattedFiles = parsedFiles.map(f => {
                            if (typeof f === 'string') {
                                return { filePath: f, name: f.split('/').pop().replace(/_\d{14}(?=\.\w+$|$)/, '') };
                            }
                            return f;
                        });
                        setFileList(formattedFiles);
                    } catch (e) {
                        const fallbackFile = record.fileLuuTru || record.FileLuuTru;
                        setFileList(fallbackFile ? [{ filePath: fallbackFile, name: fallbackFile.split('/').pop().replace(/_\d{14}(?=\.\w+$|$)/, '') }] : []);
                    }
                } else {
                    setExistingRecord(null);
                    setFileList([]);
                }
            }
        }
    );

    const createMutation = useMutation((payload) => Post(HOSOLUUTRU_API.CREATE_ITEM, null, payload));
    const updateMutation = useMutation(({ id, payload }) => Put(HOSOLUUTRU_API.UPDATE_ITEM, id, payload));

    const handleFilesChange = (newFiles) => {
        setFileList(newFiles);
    };

    const handleSave = () => {
        if (!dotTuyenSinhId) return;
        setIsSaving(true);

        const filePaths = fileList.map(f => f.filePath || f.url || f.name || f); // format file strings appropriately
        const payload = {
            DotTuyenSinhId: dotTuyenSinhId,
            FileLuuTru: JSON.stringify(filePaths)
        };

        if (existingRecord) {
            updateMutation.mutate(
                { id: existingRecord.id || existingRecord.Id, payload },
                {
                    onSuccess: (res) => {
                        setIsSaving(false);
                        if (res.status === 200 || res.status === 204) {
                            toast.success('Lưu trữ hồ sơ thành công');
                            onClose();
                        } else {
                            toast.error('Có lỗi xảy ra khi cập nhật hồ sơ');
                        }
                    },
                    onError: () => {
                        setIsSaving(false);
                        toast.error('Có lỗi xảy ra khi cập nhật hồ sơ');
                    }
                }
            );
        } else {
            createMutation.mutate(payload, {
                onSuccess: (res) => {
                    setIsSaving(false);
                    if (res.status === 200 || res.status === 201) {
                        toast.success('Lưu trữ hồ sơ thành công');
                        onClose();
                    } else {
                        toast.error('Có lỗi xảy ra khi tạo mới hồ sơ');
                    }
                },
                onError: () => {
                    setIsSaving(false);
                    toast.error('Có lỗi xảy ra khi tạo mới hồ sơ');
                }
            });
        }
    };

    return (
        <Modal 
            show={isOpen} 
            onHide={onClose} 
            size="lg" 
            backdrop="static" 
            keyboard={false}
            centered
            style={{ zIndex: '9999' }}
        >
            <Modal.Header closeButton>
                <Modal.Title>Lưu trữ hồ sơ - {tenDot}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-4">Đang tải dữ liệu...</div>
                ) : (
                    <DocumentInput
                        value={fileList}
                        multiple={true}
                        onFilesChange={handleFilesChange}
                        labelTitle="Tài liệu đợt tuyển sinh"
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                    Đóng
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaving || isLoading}>
                    {isSaving ? 'Đang lưu...' : 'Lưu lại'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default HoSoLuuTruModal;
